import { EntryModel, type NetworkEntries, type PostEntry } from '@/models/entry';
import { signedApiClient } from './apiClient';
import type { FormModel } from '@/models/form';
import type { CaseModel } from '@/models/case';
import { ActivityModel, type NetworkActivity } from '@/models/activity';

class EntryService {

  private studyEndpoint: string = '';
  initService(studyEndpoint: string) {
    this.studyEndpoint = studyEndpoint;
  }

  async getCaseEntries(studyId: string, caseId: string, page: number, limit: number, sortField?: string, sortOrder?: string) {
    let uri = `${this.studyEndpoint}/${studyId}/entries?caseIdentifier=${caseId}&page=${page}&limit=${limit}`;
    if (sortField) {
      uri += `&sortBy=${sortField}`;
      if (sortOrder) {
        uri += `&sortOrder=${sortOrder}`;
      }
    }
    const apiClient = signedApiClient('GET', uri);
    const entriesResponse = await apiClient.get(uri);
    const networkResponse: NetworkEntries = entriesResponse.data;
    return {
      entries: networkResponse.entries.map(entry => EntryModel.mapFromBackend(entry)),
      entriesCount: networkResponse.entriesCount
    };
  }

  async getStudyEntries(cases: CaseModel[], studyId: string, page: number,
    limit: number, sortField?: string, sortOrder?: string) {
    let uri = `${this.studyEndpoint}/${studyId}/entries?page=${page}&limit=${limit}`;
    if (sortField) {
      uri += `&sortBy=${sortField}`;
      if (sortOrder) {
        uri += `&sortOrder=${sortOrder}`;
      }
    }
    const apiClient = signedApiClient('GET', uri);
    const entriesResponse = await apiClient.get(uri);
    const networkResponse: NetworkEntries = entriesResponse.data;
    return {
      entries: networkResponse.entries.map(entry => EntryModel.mapFromBackend(entry, cases)),
      entriesCount: networkResponse.entriesCount
    };
  }

  async getArchivedEntries(studyId: string) {
    const uri = `${this.studyEndpoint}/${studyId}/entries?type=archive`;
    const apiClient = signedApiClient('GET', uri);
    const entriesResponse = await apiClient.get(uri);
    const networkResponse: NetworkEntries = entriesResponse.data;
    return {
      entries: networkResponse.entries.map(entry => EntryModel.mapFromBackend(entry)),
      entriesCount: networkResponse.entriesCount
    };
  }

  async getCaseArchivedEntries(studyId: string, caseId: string) {
    const uri = `${this.studyEndpoint}/${studyId}/entries?caseIdentifier=${caseId}&type=archive`;
    const apiClient = signedApiClient('GET', uri);
    const entriesResponse = await apiClient.get(uri);
    const networkResponse: NetworkEntries = entriesResponse.data;
    return {
      entries: networkResponse.entries.map(entry => EntryModel.mapFromBackend(entry)),
      entriesCount: networkResponse.entriesCount
    };
  }

  async updateEntry(entryId: string, payload: PostEntry) {
    const uri = `response/${entryId}`;
    const apiClient = signedApiClient('PATCH', uri);
    return await apiClient.patch(uri, payload);
  }

  async getEntry(entryId: string, form?: FormModel) {
    const uri = `entry/${entryId}`;
    const apiClient = signedApiClient('GET', uri);
    const entry = await apiClient.get(uri);
    return EntryModel.mapWithItems(entry.data, form);
  }

  async getEntryByToken(token: string, form?: FormModel) {
    const uri = `entry/${token}?token=true`;
    const apiClient = signedApiClient('GET', uri);
    const entry = await apiClient.get(uri);
    if (entry.data) {
      return EntryModel.mapWithItems(entry.data, form);
    }
    return;
  }

  async createEntry(payload: PostEntry) {
    const uri = `response`;
    const apiClient = signedApiClient('POST', uri);
    return await apiClient.post(uri, payload);
  }

  async getEntryActivities(entryIdentifier: string) {
    const uri = `entry/${entryIdentifier}/activities`;
    const apiClient = signedApiClient('GET', uri);
    const activityResponse = await apiClient.get(uri);
    const networkActivity: NetworkActivity[] = activityResponse.data.activities;
    return networkActivity.map(activityItem => ActivityModel.mapFromBackend(activityItem));
  }

  async cloneEntry(entryId: string) {
    const uri = `entry/${entryId}/clone`;
    const apiClient = signedApiClient('POST', uri);
    return await apiClient.post(uri);
  }
}

export default new EntryService();
